import { site } from '@shared/site.config';
import { useTitle } from '@/lib/useTitle';
import { useJsonLd } from '@/lib/useJsonLd';
import { faqPageJsonLd } from '@/lib/seo';
import LocalPageSheet from '@/components/LocalPageSheet';
import type { Faq } from '@/components/LocalPageSheet';

export const meta = {
  path: '/industrial-space-for-lease-cambridge',
  title: 'Industrial Space for Lease — Cambridge, ON',
  description:
    'Landlord and tenant representation for industrial and flex space in Cambridge, Ontario, along the 401 corridor. Direct line to Pinnacle Commercial, no listings portal.',
};

const cambridge = site.serviceAreas.find((a) => a.slug === 'cambridge')!;

const FAQS: Faq[] = [
  {
    q: 'Do you have a list of available industrial space in Cambridge?',
    a: "No public listings page — for a reason. A list like that is out of date within days, and it tells every other broker in the region exactly what's in play. A direct conversation gets you the real current picture instead.",
  },
  {
    q: 'What lease structures are typical for industrial space?',
    a: "Most industrial leases here are net (NNN) — base rent plus your share of taxes, insurance and common-area costs — though gross and semi-gross terms do come up. I'll walk you through exactly what you're agreeing to before you sign anything.",
  },
  {
    q: 'Do you represent landlords, or only tenants?',
    a: "Both, though never on the same deal at the same time. Whichever side you're on, you get direct representation, not a split-loyalty arrangement.",
  },
  {
    q: 'How fast can I get in front of available space?',
    a: 'Usually the same day. One call gets you a shortlist based on what actually exists right now, including space I know about before it’s formally marketed.',
  },
];

export default function IndustrialCambridge() {
  useTitle(meta.title, meta.description);
  useJsonLd('faq-industrial-cambridge', faqPageJsonLd(FAQS));

  return (
    <LocalPageSheet
      sheetNo="S-01"
      kicker="Local search — industrial leasing"
      h1="Industrial space for lease, Cambridge."
      intro="Landlord and tenant representation for industrial and flex space in Cambridge — inside the same four-city practice, not a satellite covering it from somewhere else."
      glyph="industrial"
      audiences={[
        {
          label: 'Looking for space',
          body: "Tell me the size, the clear height, the loading you need and the timeline, and I'll tell you what's realistic in Cambridge right now — including space that isn't advertised anywhere yet.",
          ctaLabel: 'Tell me what you need',
        },
        {
          label: 'Have space to lease',
          body: 'Positioning, marketing and tenant sourcing for vacant industrial space — direct to qualified tenants, not a blast to every list broker in the region.',
          ctaLabel: 'Talk through your space',
        },
      ]}
      localLabel="Cambridge, industrial"
      localBody={
        <>
          <p>{cambridge.blurb}</p>
          <p>
            The 401/Highway 8 interchange puts most of the city's industrial base within a short haul of the rest
            of the Waterloo Region and the GTA, which is a large part of why tenants tend to stay once they're in.
          </p>
        </>
      }
      honestLabel="No listings page, on purpose"
      honestBody="Available industrial space turns over fast, and a static list is wrong the day after it's posted. Call or send a message with what you need and you'll get the actual current picture — including space that's about to come available but isn't public yet."
      faqs={FAQS}
      interest="leasing"
      closingLine="Industrial space in Cambridge — as a tenant or a landlord."
      related={[
        { to: '/industrial-space-for-lease-cambridge-guide', label: 'Guide — costs & steps for tenants' },
        { to: '/leasing', label: 'Full leasing services' },
        { to: '/retail-space-for-lease-uptown-waterloo-downtown-kitchener', label: 'Retail — Uptown Waterloo / Downtown Kitchener' },
        { to: '/office-space-for-lease-kitchener-waterloo', label: 'Office — Kitchener & Waterloo' },
      ]}
    />
  );
}
